import { createAction, createReducer } from "@reduxjs/toolkit"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { api } from "./helpers/api"
import { PAGINATION_INITIAL_STATE } from "./constants"

// Update Experience actions
export const requestingUpdateExperience = createAction('questionnaire/requestingUpdateExperience')
export const updateExperienceSuccess = createAction('questionnaire/updateExperienceSuccess')
export const updateExperienceFailure = createAction('questionnaire/updateExperienceFailure')

// Questionnaire Template Experience List actions
export const requestingQuestionnaireTemplateExperienceList = createAction('questionnaire/requestingQuestionnaireTemplateExperienceList')
export const receivedQuestionnaireTemplateExperienceList = createAction('questionnaire/receivedQuestionnaireTemplateExperienceList')
export const failedQuestionnaireTemplateExperienceList = createAction('questionnaire/failedQuestionnaireTemplateExperienceList')

// ACTIONS
export const requestedBookingQuestionnaire = createAction("question/requestedBookingQuestionnaire")
export const receivedBookingQuestionnaire = createAction("question/receivedBookingQuestionnaire")
export const failedBookingQuestionnaire = createAction("question/failedBookingQuestionnaire")

export const submittingQuestionnaire = createAction("question/submittingQuestionnaire")
export const submitQuestionnaireSuccess = createAction("question/submitQuestionnaireSuccess")
export const submitQuestionnaireFailure = createAction("question/submitQuestionnaireFailure")

export const updatingQuestionnaireResponses = createAction("question/updatingQuestionnaireResponses")
export const updateQuestionnaireResponsesSuccess = createAction("question/updateQuestionnaireResponsesSuccess")
export const updateQuestionnaireResponsesFailure = createAction("question/updateQuestionnaireResponsesFailure")

export const requestedQuestionnaireTemplates = createAction("question/requestedQuestionnaireTemplates")
export const receivedQuestionnaireTemplates = createAction("question/receivedQuestionnaireTemplates")
export const failedQuestionnaireTemplates = createAction("question/failedQuestionnaireTemplates")

export const requestedQuestionnaireTemplate = createAction("question/requestedQuestionnaireTemplate")
export const receivedQuestionnaireTemplate = createAction("question/receivedQuestionnaireTemplate")
export const failedQuestionnaireTemplate = createAction("question/failedQuestionnaireTemplate")

export const requestingSaveTemplateQuestions = createAction("question/requestingSaveTemplateQuestions")
export const saveTemplateQuestionsSuccess = createAction("question/saveTemplateQuestionsSuccess")
export const saveTemplateQuestionsFailure = createAction("question/saveTemplateQuestionsFailure")

export const requestingCreateTemplate = createAction("question/requestingCreateTemplate")
export const createTemplateSuccess = createAction("question/createTemplateSuccess")
export const createTemplateFailure = createAction("question/createTemplateFailure")

export const requestingUpdateTemplate = createAction("question/requestingUpdateTemplate")
export const updateTemplateSuccess = createAction("question/updateTemplateSuccess")
export const updateTemplateFailure = createAction("question/updateTemplateFailure")

export const requestingDuplicateTemplate = createAction("question/requestingDuplicateTemplate")
export const duplicateTemplateSuccess = createAction("question/duplicateTemplateSuccess")
export const duplicateTemplateFailure = createAction("question/duplicateTemplateFailure")

export const requestingDeleteTemplate = createAction("question/requestingDeleteTemplate")
export const deleteTemplateSuccess = createAction("question/deleteTemplateSuccess")
export const deleteTemplateFailure = createAction("question/deleteTemplateFailure")

export const addQuestion = createAction("question/addQuestion", (question) => ({
  payload: {
    ...question,
    id: Date.now(),
  required: question.required === undefined ? true : question.required,
    options: question.options || [],
    dropdownOptions: question.dropdownOptions || [],
    radioOptions: question.radioOptions || [],
    selectedRadioOption: ''
  }
}))

export const updateQuestion = createAction("question/updateQuestion")
export const deleteQuestion = createAction("question/deleteQuestion")
export const reorderQuestions = createAction("question/reorderQuestions")
export const resetQuestions = createAction("question/resetQuestions")

// REDUCER
const initialState = {
  ...LOADING_INITIAL_STATE,
  questions: [],
  questionnaireTemplates: {
    ...LOADING_INITIAL_STATE,
    data: [],
  },
  bookingQuestionnaire: {
    ...LOADING_INITIAL_STATE,
    data: null,
  },
  currentTemplate: {
    ...LOADING_INITIAL_STATE,
    data: null,
  },
  templateQuestions: {
    // ...LOADING_INITIAL_STATE,
    data: null,
  },
  deleteTemplate: {
    loading: false,
    error: null
  },
  metadata:{
    ...PAGINATION_INITIAL_STATE,
  },
  questionaire_data:{
    ...LOADING_INITIAL_STATE,
    data: null,
  },
  experienceUpdate: {
    ...LOADING_INITIAL_STATE,
    templateId: null
  },
  templateExperienceList: {
    ...LOADING_INITIAL_STATE,
    data: []
  },
  updateQuestionnaireResponses: {
    ...LOADING_INITIAL_STATE,
    data: null
  }
}

const questionReducer = createReducer(initialState, {
  // Booking Questionnaire
  [requestedBookingQuestionnaire.type]: (state) => {
    setRequested(state.questionaire_data)
    state.questionaire_data.error = null
  },
  [receivedBookingQuestionnaire.type]: (state, action) => {
    setReceived(state.questionaire_data)
    state.questionaire_data.error = null
    state.questionaire_data.data = action.payload
  },
  [failedBookingQuestionnaire.type]: (state, action) => {
    setFailed(state.questionaire_data)
    state.questionaire_data.error = action.payload || 'Failed to fetch booking questionnaire'
    state.questionaire_data.data = null
  },

  // Questionnaire Templates
  [requestedQuestionnaireTemplates.type]: (state) => {
    setRequested(state.questionnaireTemplates)
  },
  [receivedQuestionnaireTemplates.type]: (state, action) => {
    setReceived(state.questionnaireTemplates)
    state.questionnaireTemplates.data = action.payload.data
    state.metadata = action.payload.metadata
  },
  [failedQuestionnaireTemplates.type]: (state) => {
    setFailed(state)
  },
  
  // Single Questionnaire Template
  [requestedQuestionnaireTemplate.type]: (state) => {
    setRequested(state.currentTemplate)
  },
  [receivedQuestionnaireTemplate.type]: (state, action) => {
    setReceived(state.currentTemplate)
    state.currentTemplate.error = null
    state.currentTemplate.data = action.payload.questionnaire_template || action.payload
    state.templateQuestions.data = action.payload.data || action.payload
  },
  [failedQuestionnaireTemplate.type]: (state, action) => {
    setFailed(state.currentTemplate)
    state.currentTemplate.error = action.payload || 'Failed to fetch template'
  },
  
  // Create Template
  [requestingCreateTemplate.type]: (state) => {
    setRequested(state)
    state.error = null
  },
  [createTemplateSuccess.type]: (state, action) => {
    setReceived(state)
    state.error = null
    const response = action.payload || {}
    const newTemplate = response.data || response
    
    if (Array.isArray(state.questionnaireTemplates.data)) {
      state.questionnaireTemplates.data.unshift(newTemplate)
    } else {
      state.questionnaireTemplates.data = [newTemplate]
    }
  },
  [createTemplateFailure.type]: (state, action) => {
    setFailed(state)
    state.error = action.payload || 'Failed to create template'
  },


  [requestingSaveTemplateQuestions.type]: (state) => {
    setRequested(state)
    state.error = null
  },
  [saveTemplateQuestionsSuccess.type]: (state, action) => {
    setReceived(state)
    state.error = null
  },
  [saveTemplateQuestionsFailure.type]: (state, action) => {
    setFailed(state)
    state.error = action.payload || 'Failed to create template'
  },
  
  // Update template actions
  [requestingUpdateTemplate.type]: (state) => {
    setRequested(state)
    state.error = null
  },
  [updateTemplateSuccess.type]: (state, action) => {
    setReceived(state)
    state.error = null
    const response = action.payload || {}
    const updatedTemplate = response.data || response
    
    if (Array.isArray(state.questionnaireTemplates.data)) {
      const index = state.questionnaireTemplates.data.findIndex(t => t.id === updatedTemplate.id)
      if (index !== -1) {
        state.questionnaireTemplates.data[index] = updatedTemplate
      }
    }
  },
  [updateTemplateFailure.type]: (state, action) => {
    setFailed(state)
    state.error = action.payload || 'Failed to update template'
  },
  
  // Delete template reducers
  [requestingDeleteTemplate.type]: (state) => {
    setRequested(state.deleteTemplate)
    state.error = null
  },
  [deleteTemplateSuccess.type]: (state, action) => {
    setReceived(state.deleteTemplate)
    state.error = null
    const templateId = action.payload?.id || action.meta?.arg
    if (templateId && Array.isArray(state.questionnaireTemplates.data)) {
      state.questionnaireTemplates.data = state.questionnaireTemplates.data.filter(
        template => template.id !== templateId
      )
    }
  },
  [deleteTemplateFailure.type]: (state, action) => {
    setFailed(state.deleteTemplate)
    state.error = action.payload || 'Failed to delete template'
  },
  
  // Update Experience
  [requestingUpdateExperience.type]: (state) => {
    setRequested(state.questionnaireTemplates)
    state.error = null
  },
  [updateExperienceSuccess.type]: (state, action) => {
    setReceived(state.questionnaireTemplates)
    state.error = null
    // Update the specific template in the list if it exists
    const updatedTemplate = action.payload
    if (updatedTemplate && Array.isArray(state.questionnaireTemplates?.data)) {
      const index = state.questionnaireTemplates.data.findIndex(t => t.id === updatedTemplate.id)
      if (index !== -1) {
        state.questionnaireTemplates.data[index] = updatedTemplate
      }
    }
  },
  [updateExperienceFailure.type]: (state, action) => {
            setFailed(state)
    state.error = action.payload || 'Failed to update experience'
    state.templateId = null
  },
  
  // Questionnaire Template Experience List
  [requestingQuestionnaireTemplateExperienceList.type]: (state) => {
    setRequested(state.templateExperienceList)
    state.templateExperienceList.error = null
  },
  [receivedQuestionnaireTemplateExperienceList.type]: (state, action) => {
    setReceived(state.templateExperienceList)
    state.templateExperienceList.error = null
    state.templateExperienceList.data = action.payload.data || []
  },
  [failedQuestionnaireTemplateExperienceList.type]: (state, action) => {
    setFailed(state.templateExperienceList)
    state.templateExperienceList.error = action.payload || 'Failed to fetch template experience list'
    state.templateExperienceList.data = []
  },
  
  // Questions
  [addQuestion.type]: (state, action) => {
    state.questions.push(action.payload)
  },
  [updateQuestion.type]: (state, action) => {
    const { id, updates } = action.payload
    const index = state.questions.findIndex(q => q.id === id)
    if (index !== -1) {
      state.questions[index] = { ...state.questions[index], ...updates }
    }
  },
  [deleteQuestion.type]: (state, action) => {
    state.questions = state.questions.filter(q => q.id !== action.payload)
  },
  [reorderQuestions.type]: (state, action) => {
    state.questions = action.payload
  },
  
  // Questionnaire Submission
  [submittingQuestionnaire.type]: (state) => {
    setRequested(state)
    state.error = null
  },
  [submitQuestionnaireSuccess.type]: (state) => {
    setReceived(state)
  },
  [submitQuestionnaireFailure.type]: (state, action) => {
    setFailed(state)
    state.error = action.payload || 'Failed to submit questionnaire'
  },

  // Update Questionnaire Responses
  [updatingQuestionnaireResponses.type]: (state) => {
    setRequested(state.updateQuestionnaireResponses)
    state.updateQuestionnaireResponses.error = null
  },
  [updateQuestionnaireResponsesSuccess.type]: (state, action) => {
    setReceived(state.updateQuestionnaireResponses)
    state.updateQuestionnaireResponses.error = null
    state.updateQuestionnaireResponses.data = action.payload
  },
  [updateQuestionnaireResponsesFailure.type]: (state, action) => {
    setFailed(state.updateQuestionnaireResponses)
    state.updateQuestionnaireResponses.error = action.payload || 'Failed to update questionnaire responses'
    state.updateQuestionnaireResponses.data = null
  },
  [resetQuestions.type]: () => initialState
})

export default questionReducer

// Thunk action for fetching questionnaire templates
export const getQuestionnaireTemplates = (page, pageSize , search = '') => {
  const params = {
    page,
    per_page: pageSize,
    search: search.trim()
  }
  
  return api({
    url: '/guide/questionnaire_templates',
    method: 'get',
    params,
    onStart: requestedQuestionnaireTemplates.type,
    onSuccess: receivedQuestionnaireTemplates.type,
    onError: failedQuestionnaireTemplates.type
  })
}

// Thunk action for creating a new questionnaire template
export const createQuestionnaireTemplate = (templateData) => {
  return api({
    url: '/guide/questionnaire_templates',
    method: 'post',
    data: templateData,
    onStart: requestingCreateTemplate.type,
    onSuccess: createTemplateSuccess.type,
    onError: createTemplateFailure.type
  })
}

// Thunk action for updating an existing questionnaire template
export const updateQuestionnaireTemplate = (templateId, templateData) => {
  return api({
    url: `/guide/questionnaire_templates/${templateId}`,
    method: 'put',
    data: templateData,
    onStart: requestingUpdateTemplate.type,
    onSuccess: updateTemplateSuccess.type,
    onError: updateTemplateFailure.type
  })
}
// Thunk action for deleting a questionnaire template
export const deleteQuestionnaireTemplate = (templateId) => {
  return api({
    url: `/guide/questionnaire_templates/${templateId}`,
    method: 'delete',
    onStart: requestingDeleteTemplate.type,
    onSuccess: deleteTemplateSuccess.type,
    onError: deleteTemplateFailure.type,
    successMessage: 'Template deleted successfully',
    errorMessage: 'Failed to delete template'
  })
}

// Thunk action for fetching a single questionnaire template by ID
export const getQuestionnaireTemplateById = (templateId) => {
  return api({
    url: `/guide/questionnaire_templates/${templateId}/questions`,
    method: 'get',
    onStart: requestedQuestionnaireTemplate.type,
    onSuccess: receivedQuestionnaireTemplate.type,
    onError: failedQuestionnaireTemplate.type,
    errorMessage: 'Failed to fetch template details'
  })
}

// Thunk action for saving questionnaire template questions
export const saveQuestionnaireTemplateQuestions = (questions) => {
  return api({
    url: `/guide/questionnaire_templates`,
    method: 'post',
    data:  questions ,
    onStart: requestingSaveTemplateQuestions.type,
    onSuccess: saveTemplateQuestionsSuccess.type,
    onError: saveTemplateQuestionsFailure.type,
    successMessage: 'Questions saved successfully',
    errorMessage: 'Failed to save questions'
  })
}
export const updateQuestionnaireTemplateQuestions = (templateId, questions) => {
  return api({
    url: `/guide/questionnaire_templates/${templateId}`,
    method: 'put',
    data: questions,
    onStart: requestingSaveTemplateQuestions.type,
    onSuccess: saveTemplateQuestionsSuccess.type,
    onError: saveTemplateQuestionsFailure.type,
    successMessage: 'Questions updated successfully',
    errorMessage: 'Failed to update questions'
  })
}

// Thunk action for fetching booking questionnaire by ID
export const getBookingQuestionnaire = (bookingId) => {
  return api({
    url: `/booking_questionnaires/${bookingId}`,
    method: 'get',
    onStart: requestedBookingQuestionnaire.type,
    onSuccess: receivedBookingQuestionnaire.type,
    onError: failedBookingQuestionnaire.type,
    errorMessage: 'Failed to fetch booking questionnaire'
  })
}

export const duplicateQuestionnaireTemplate = (templateId, options = {}) => {
    return api({
    url: `/guide/questionnaire_templates/${templateId}/duplicate`,
    method: 'post',
    data: options,
    onStart: requestingDuplicateTemplate.type,
    onSuccess: duplicateTemplateSuccess.type,
    onError: duplicateTemplateFailure.type,
    successMessage: 'Template duplicated successfully',
    errorMessage: 'Failed to duplicate template'
  })
}

/**
 * Thunk action for updating experience for a questionnaire template
 * @param {string} templateId - The ID of the template
 * @param {Object} experienceData - The experience data to update
 * @returns {Promise} - Resolves with the updated template data
 */
export const updateQuestionnaireTemplateExperience = (templateId, experienceData) => {
  return api({
    url: `/guide/questionnaire_templates/${templateId}/update_experience`,
    method: 'put',
    data: experienceData,
    onStart: requestingUpdateExperience.type,
    onSuccess: updateExperienceSuccess.type,
    onError: updateExperienceFailure.type,
    successMessage: 'Experience updated successfully',
    errorMessage: 'Failed to update experience'
  })
}

/**
 * Thunk action for fetching questionnaire template experience list
 * @returns {Promise} - Resolves with the experience list data
 */
export const getQuestionnaireTemplateExperienceList = () => {
  return api({
    url: '/guide/trips/questionnaire_template_experience_list',
    method: 'get',
    onStart: requestingQuestionnaireTemplateExperienceList.type,
    onSuccess: receivedQuestionnaireTemplateExperienceList.type,
    onError: failedQuestionnaireTemplateExperienceList.type,
    errorMessage: 'Failed to fetch template experience list'
  })
}

/**
 * Thunk action for submitting questionnaire responses
 * @param {number} questionnaireId - The ID of the questionnaire to submit
 * @param {Object} responses - The questionnaire responses to submit
 * @returns {Promise} - Resolves with the API response data
 */
export const submitQuestionnaire = (questionnaireId,answers) => {
  return api({
    url: `/booking_questionnaires/${questionnaireId}/submit_questionnaire`,
    method: 'post',
    data: {answers},
    onStart: submittingQuestionnaire.type,
    onSuccess: submitQuestionnaireSuccess.type,
    onError: submitQuestionnaireFailure.type,
    successMessage: 'Questionnaire submitted successfully',
    errorMessage: 'Failed to submit questionnaire'
  })
}

/**
 * Thunk action for updating questionnaire responses
 * @param {number} bookingWaiverId - The booking waiver ID
 * @param {Object} responses - The questionnaire responses to update
 * @returns {Promise} - Resolves with the API response data
 */
export const updateQuestionnaireResponses = (bookingWaiverId, responses) => {
  return api({
    url: `/booking_questionnaires/${bookingWaiverId}/update_questionnaire_responses`,
    method: 'put',
    data: responses,
    onStart: updatingQuestionnaireResponses.type,
    onSuccess: updateQuestionnaireResponsesSuccess.type,
    onError: updateQuestionnaireResponsesFailure.type,
    successMessage: 'Questionnaire responses updated successfully',
    errorMessage: 'Failed to update questionnaire responses'
  })
}
